﻿<template>
    <div>
        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <!-- Header ordine -->
        <div class="p-6 pb-0">
            <div class="bg-white border border-gray-200 rounded-lg p-4 flex flex-wrap gap-6 text-sm">
                <div>
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Ordine</span>
                    <span class="font-semibold text-gray-800">{{ ordine.InternalOrdNo }}</span>
                </div>
                <div>
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Cliente</span>
                    <span class="font-semibold text-gray-800">{{ ordine.CompanyName || ordine.Customer || '-' }}</span>
                </div>
                <div>
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Data ordine</span>
                    <span class="font-semibold text-gray-800">{{ formatDate(ordine.OrderDate) }}</span>
                </div>
                <div v-if="ordine.job_description">
                    <span class="text-gray-500 block text-xs uppercase tracking-wide">Commessa</span>
                    <span class="font-semibold text-gray-800">{{ ordine.job_description }}</span>
                </div>
            </div>
        </div>

        <div class="p-6 flex gap-6">
            <!-- Colonna sinistra: righe ordine -->
            <div class="w-1/3 flex-shrink-0">
                <h2 class="text-base font-semibold text-gray-700 mb-3">Righe ordine</h2>
                <div class="space-y-2">

                    <!-- Riepilogo ordine -->
                    <div @click="attivaRecap"
                        :class="[
                            'border rounded-lg p-3 cursor-pointer transition-colors text-sm',
                            showRecap
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        ]">
                        <div class="flex items-center gap-2">
                            <svg class="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                            </svg>
                            <div class="font-medium text-indigo-700">Riepilogo ordine</div>
                        </div>
                        <div class="mt-1">
                            <span class="text-xs bg-indigo-100 text-indigo-700 rounded px-1.5 py-0.5">
                                {{ tutteLeAttivitaFlat.length }} attività totali
                            </span>
                        </div>
                    </div>

                    <!-- Attività non legate a una riga -->
                    <div @click="selectRiga(null)"
                        :class="[
                            'border rounded-lg p-3 cursor-pointer transition-colors text-sm',
                            selectedRiga === null && !showRecap
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        ]">
                        <div class="font-medium text-gray-500 italic">Attività generali ordine</div>
                        <div class="mt-1">
                            <span class="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
                                {{ attivitaSenzaRiga.length }} attività
                            </span>
                        </div>
                    </div>

                    
                    <div v-for="riga in righe" :key="riga.row_id"
                        @click="selectRiga(riga)"
                        :class="[
                            'border rounded-lg p-3 cursor-pointer transition-colors text-sm',
                            selectedRiga && selectedRiga.row_id === riga.row_id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        ]">
                        
                        <div class="text-xs text-gray-500 mt-1">
                            <span class="font-mono">{{ riga.Item }}</span>
                            <span v-if="riga.ConfirmedDeliveryDate"
                            class="ml-2"
                            :class="dateStatus(riga.ConfirmedDeliveryDate)">
                            · consegna {{ formatDate(riga.ConfirmedDeliveryDate) }} </span>
                        </div>
                        <div class="mt-1">
                            <span class="text-xs bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">
                                {{ attivitaPerRiga(riga).length }} attività
                            </span>
                        </div>
                        <div class="text-sm text-gray-500 mt-1">
                            <div class="font-medium text-gray-800 truncate min-w-0">
                                {{ riga.Description || riga.Item }}
                            </div>

                            <button
                                type="button"
                                @click.stop="apriRegistroModal(riga)"
                                title="Inserisci in Registri Personalizzati"
                                class="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5
                                    text-xs font-medium text-purple-700 bg-purple-50
                                    border border-purple-200 rounded-md
                                    hover:bg-purple-100 hover:border-purple-300
                                    focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
                                    transition-colors"
                            >
                                <v-icon
                                    name="hi-solid-collection"
                                    class="w-4 h-4"
                                />

                                <span>Aggiungi a Registro</span>
                            </button>
                        </div>
                    </div>

                    
                </div>
            </div>

            <!-- Colonna destra: albero attività -->
            <div class="flex-1 min-w-0">

                <!-- Vista riepilogo -->
                <template v-if="showRecap">
                    <h2 class="text-base font-semibold text-indigo-700 mb-4">Riepilogo ordine {{ ordine.InternalOrdNo }}</h2>

                    <div v-if="tutteLeAttivitaFlat.length === 0" class="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
                        Nessuna attività per questo ordine.
                    </div>

                    <div v-else class="space-y-6">
                        <div v-for="gruppo in attivitaRaggruppate" :key="gruppo.label">
                            <div v-if="gruppo.attivita.length > 0" class="flex items-center gap-3 mb-2">
                                <div class="h-px flex-1 bg-gray-200"></div>
                                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                                    {{ gruppo.label }}
                                </span>
                                <div class="h-px flex-1 bg-gray-200"></div>
                            </div>
                            
                            <div class="space-y-2">
                                <ActivityNode
                                    v-for="attivita in gruppo.attivita"
                                    :key="attivita.id"
                                    :attivita="attivita"
                                    :all-attivita="tutteLeAttivitaFlat"
                                    :ordine="ordine"
                                    :riga="null"
                                    :depth="0"
                                    @salva="onSalva"
                                    @elimina="onElimina"
                                    @aggiorna-parent="onAggiornaParent"
                                    @aggiungi-figlio="onAggiungiAttivita"
                                />
                            </div>
                        </div>
                    </div>
                </template>

                <!-- Vista normale per riga -->
                <template v-else>
                    <div class="flex items-center justify-between mb-3">
                        <h2 class="text-base font-semibold text-gray-700">
                            <span v-if="selectedRiga">
                                Attività — {{ selectedRiga.Description || selectedRiga.Item }}
                            </span>
                            <span v-else>Attività generali ordine</span>
                        </h2>
                        <button @click="aggiungiAttivitaRadice" class="p93__btn_primary text-sm">
                            + Nuova attività
                        </button>
                    </div>

                    <div v-if="attivitaVisibili.length === 0" class="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-lg">
                        Nessuna attività. Clicca "Nuova attività" per iniziare.
                    </div>

                    <div class="space-y-2">
                        <ActivityNode
                            v-for="attivita in attivitaVisibili"
                            :key="attivita.id"
                            :attivita="attivita"
                            :all-attivita="tutteLeAttivitaFlat"
                            :ordine="ordine"
                            :riga="selectedRiga"
                            :depth="0"
                            @salva="onSalva"
                            @elimina="onElimina"
                            @aggiorna-parent="onAggiornaParent"
                            @aggiungi-figlio="onAggiungiAttivita"
                        />
                    </div>
                </template>
            </div>
        </div>

        <!-- Modal Registri Personalizzati -->
        <div
            v-if="showRegistroModal"
            class="fixed inset-0 z-50 bg-gray-900 bg-opacity-40 flex items-center justify-center mt-10"
            @click="chiudiRegistroModal"
        >
            <div class="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" @click.stop>
                <div class="border-b border-gray-200 px-6 py-4 flex items-start justify-between gap-4">
                    <div>
                        <h2 class="text-lg font-semibold text-gray-900">Inserisci in Registri Personalizzati</h2>
                        <p v-if="rigaRegistroCorrente" class="text-sm text-gray-500 mt-0.5 truncate">
                            Riga: {{ rigaRegistroCorrente.Description || rigaRegistroCorrente.Item }}
                        </p>
                    </div>
                    <button type="button" @click="chiudiRegistroModal" class="flex-shrink-0 rounded-full p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>

                <div class="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    <div v-if="loadingRegistri" class="text-center py-6 text-gray-400 text-sm">Caricamento registri...</div>

                    <template v-else>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-1">Registro *</label>
                            <select
                                v-model="selectedRegistroId"
                                @change="onRegistroChange"
                                class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                                <option :value="null">— Seleziona un registro —</option>
                                <option v-for="r in registri" :key="r.id" :value="r.id">{{ r.name }}</option>
                            </select>
                            
                        </div>

                        <!-- Storico records per il cliente corrente -->
                        <div v-if="registroCustomerColumnId !== null">
                            <div class="flex items-center gap-2 mb-2">
                                <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    Storico — {{ ordine.CompanyName || ordine.Customer }}
                                </span>
                                <span v-if="loadingRegistroRecords" class="text-xs text-gray-400">caricamento...</span>
                                <span v-else class="text-xs text-gray-400">({{ registroRecordEsistenti.length }} record)</span>
                            </div>

                            <div v-if="loadingRegistroRecords" class="py-3 text-center text-gray-400 text-xs border border-gray-200 rounded-md">
                                Caricamento...
                            </div>

                            <div v-else-if="registroRecordEsistenti.length > 0" class="border border-gray-200 rounded-md overflow-hidden">
                                <div class="overflow-x-auto max-h-44 overflow-y-auto">
                                    <table class="w-full text-xs">
                                        <thead class="bg-gray-50 sticky top-0 z-10">
                                            <tr>
                                                <th
                                                    v-for="col in registroColumnsForTable"
                                                    :key="'th-' + col.id"
                                                    class="px-2 py-1.5 text-left font-semibold text-gray-600 border-b border-gray-200 whitespace-nowrap"
                                                >{{ col.name }}</th>
                                            </tr>
                                        </thead>
                                        <tbody class="divide-y divide-gray-100">
                                            <tr v-for="row in registroRecordEsistenti" :key="row.id" class="hover:bg-gray-50">
                                                <td
                                                    v-for="col in registroColumnsForTable"
                                                    :key="'td-' + col.id + '-' + row.id"
                                                    class="px-2 py-1.5 text-gray-700 max-w-[180px] truncate"
                                                    :title="getRegistroRecordCellValue(row, col)"
                                                >{{ getRegistroRecordCellValue(row, col) }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-if="registroRecordPaginationTotal > registroRecordEsistenti.length" class="px-3 py-1.5 text-xs text-gray-400 bg-gray-50 border-t border-gray-200">
                                    e altri {{ registroRecordPaginationTotal - registroRecordEsistenti.length }} record...
                                </div>
                            </div>

                            <div v-else class="text-xs text-gray-400 italic py-2 px-1">
                                Nessun record trovato per questo cliente in questo registro.
                            </div>
                        </div>

                        <!-- Separatore -->
                        <div v-if="registroColumns.length > 0" class="flex items-center gap-3">
                            <div class="h-px flex-1 bg-gray-200"></div>
                            <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Nuovo record</span>
                            <div class="h-px flex-1 bg-gray-200"></div>
                        </div>

                        <div v-if="loadingRegistroColumns" class="text-center py-4 text-gray-400 text-sm">Caricamento colonne...</div>

                        <template v-else-if="registroColumns.length > 0">
                            <div
                                v-for="column in registroColumns"
                                :key="'rc-' + column.id"
                            >
                                <label class="block text-sm font-medium text-gray-700 mb-1">
                                    {{ column.name }}
                                    <span v-if="column.is_required" class="text-red-500">*</span>
                                </label>

                                <input
                                    v-if="column.type === 'text'"
                                    v-model="registroFormValues[column.id]"
                                    type="text"
                                    :required="column.is_required"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <input
                                    v-else-if="column.type === 'number'"
                                    v-model="registroFormValues[column.id]"
                                    type="number"
                                    :required="column.is_required"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <input
                                    v-else-if="column.type === 'date'"
                                    v-model="registroFormValues[column.id]"
                                    type="date"
                                    :required="column.is_required"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                                <div v-else-if="column.type === 'select' || column.type === 'magoquery' || column.type === 'portalquery'" class="relative">
                                    <input
                                        v-model="registroComboboxSearch[column.id]"
                                        @focus="registroComboboxOpen[column.id] = true"
                                        @blur="chiudiCombobox(column.id)"
                                        @input="onComboboxInput(column.id)"
                                        type="text"
                                        :placeholder="'Cerca ' + column.name + '...'"
                                        :required="column.is_required && !registroFormValues[column.id]"
                                        autocomplete="off"
                                        class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                    <div
                                        v-if="registroComboboxOpen[column.id] && getRegistroColumnFilteredOptions(column).length > 0"
                                        class="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
                                    >
                                        <div
                                            v-for="opt in getRegistroColumnFilteredOptions(column)"
                                            :key="opt.value"
                                            @mousedown.prevent="selezionaOpzioneCombobox(column.id, opt)"
                                            :class="[
                                                'px-3 py-2 text-sm cursor-pointer transition-colors',
                                                registroFormValues[column.id] === opt.value
                                                    ? 'bg-purple-50 text-purple-700 font-medium'
                                                    : 'hover:bg-gray-50 text-gray-800'
                                            ]"
                                        >
                                            {{ opt.label }}
                                        </div>
                                    </div>
                                    <div v-if="registroComboboxOpen[column.id] && registroComboboxSearch[column.id] && getRegistroColumnFilteredOptions(column).length === 0"
                                        class="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-sm px-3 py-2 text-sm text-gray-400">
                                        Nessun risultato
                                    </div>
                                </div>
                                <input
                                    v-else
                                    v-model="registroFormValues[column.id]"
                                    type="text"
                                    :required="column.is_required"
                                    class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>
                        </template>
                    </template>

                    <div v-if="registroFormError" class="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                        {{ registroFormError }}
                    </div>
                </div>

                <div class="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
                    <button type="button" @click="chiudiRegistroModal" class="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors">
                        Annulla
                    </button>
                    <button
                        type="button"
                        @click="salvaRegistroRecord"
                        :disabled="!selectedRegistroId || salvandoRegistro"
                        class="px-4 py-2 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {{ salvandoRegistro ? 'Salvataggio...' : 'Salva record' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Modale creazione/modifica attivita -->
        <ModaleAttivita
            ref="modaleAttivita"
            v-model="showForm"
            :attivita="editingAttivita"
            :parent-activity="parentActivityForNew"
            :ordine="ordine"
            :righe="righe"
            :default-row-id="defaultRowIdForNew"
            :all-attivita="tutteLeAttivitaFlat"
            :departments="departments"
            :can-manage-activities="canManageActivities"
            @saved="onModaleAttivitaSaved"
            @close="onModaleAttivitaClose"
        />
    </div>
</template>


<script>
import { Link, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ActivityNode from '@/Components/ActivityNode.vue';
import ModaleAttivita from '@/Pages/Modules/attivita/Attivita/ModaleAttivita.vue';
import axios from 'axios';

export default {
    props: ['ordine', 'righe', 'attivita', 'departments', 'isManager'],
    components: { Link, BreadCrumbs, ActivityNode, ModaleAttivita },
    data() {
        return {
            localAttivita: JSON.parse(JSON.stringify(this.attivita)),
            selectedRiga: null,
            selectedRigaKey:  null,
            showRecap: true,
            showForm: false,
            editingAttivita: null,
            parentActivityForNew: null,
            defaultRowIdForNew: null,
            showRegistroModal: false,
            registri: [],
            loadingRegistri: false,
            selectedRegistroId: null,
            rigaRegistroCorrente: null,
            registroColumns: [],
            registroColumnsOptions: {},
            registroFormValues: {},
            registroComboboxSearch: {},
            registroComboboxOpen: {},
            loadingRegistroColumns: false,
            registroCustomerColumnId: null,
            registroCustomerFilterValue: '',
            registroRecordEsistenti: [],
            registroRecordPaginationTotal: 0,
            loadingRegistroRecords: false,
            salvandoRegistro: false,
            registroFormError: null,
        };
    },
    watch: {
        attivita(val) {
            this.localAttivita = JSON.parse(JSON.stringify(val));
        },
    },
    computed: {
        navigation() {
            return [
                { name: this.$t('ui.dashboard'), href: route('dashboard') },
                { name: this.$t('modules.attivita'), href: route('modules.home', 'attivita') },
                { name: 'Lista ordini', href: route('modules.attivita.lista-ordini') },
                { name: 'Ordine ' + this.ordine.InternalOrdNo },
            ];
        },
        tutteLeAttivitaFlat() {
            return this.flattenAttivita(this.localAttivita);
        },
        attivitaPerRiga() {
            return (riga) => {
                return this.tutteLeAttivitaFlat.filter(a => a.row_id === riga.row_id);
            };
        },
        attivitaSenzaRiga() {
            return this.tutteLeAttivitaFlat.filter(a => !a.row_id && !a.parent_id);
        },
        attivitaVisibili() {
            if (this.selectedRiga === null) {
                return this.localAttivita.filter(a => !a.row_id);
            }
            return this.localAttivita.filter(a => a.row_id === this.selectedRiga.row_id);
        },
        canManageActivities() {
            return this.$page.props.isAdminOrSuperAdmin || this.isManager;
        },
        registroColumnsForTable() {
            return this.registroColumns.filter(c => c.id !== this.registroCustomerColumnId);
        },
        attivitaRaggruppate() {
            const gruppi = [];
            for (const riga of this.righe) {
                const attivitaRiga = this.localAttivita.filter(a => a.row_id === riga.row_id);
                gruppi.push({
                    label: riga.Description || riga.Item,
                    attivita: attivitaRiga,
                });
            }
            const generali = this.localAttivita.filter(a => !a.row_id);
            if (generali.length > 0) {
                gruppi.push({ label: 'Attività generali ordine', attivita: generali });
            }
            return gruppi;
        },
    },
    methods: {
        flattenAttivita(lista) {
            const result = [];
            const walk = (items) => {
                for (const a of items) {
                    result.push(a);
                    if (a.children && a.children.length) walk(a.children);
                }
            };
            walk(lista);
            return result;
        },
        formatDate(dateStr) {
            if (!dateStr) return '-';
            const d = new Date(dateStr);
            if (isNaN(d)) return dateStr;
            return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },
        nomeAttivita(id) {
            const a = this.tutteLeAttivitaFlat.find(x => x.id === id);
            return a ? a.title : id;
        },
        attivaRecap() {
            this.showRecap = true;
            this.selectedRiga = null;
            this.selectedRigaKey = null;
        },
        selectRiga(riga) {
            this.showRecap = false;
            this.selectedRiga = riga;
            this.selectedRigaKey = riga ? riga.row_id : null;
        },
        apriModaleAttivita({ editingAttivita = null, parentActivityForNew = null, defaultRowIdForNew = null }) {
            this.editingAttivita = editingAttivita;
            this.parentActivityForNew = parentActivityForNew;
            this.defaultRowIdForNew = defaultRowIdForNew;
            if (this.showForm) {
                this.showForm = false;
                this.$nextTick(() => { this.showForm = true; });
            } else {
                this.showForm = true;
            }
        },
        aggiungiAttivitaRadice() {
            this.apriModaleAttivita({
                defaultRowIdForNew: this.selectedRiga ? this.selectedRiga.row_id : null,
            });
        },
        onAggiungiAttivita(padre) {
            this.apriModaleAttivita({ parentActivityForNew: padre });
        },
        onSalva(attivita) {
            this.apriModaleAttivita({ editingAttivita: attivita });
        },
        onModaleAttivitaSaved({ payload } = {}) {
            const savedRowId = payload?.row_id || null;
            this.selectedRigaKey = savedRowId;
            this.selectedRiga = savedRowId
                ? (this.righe.find(r => r.row_id === savedRowId) || null)
                : null;
            this.reloadAttivita();
        },
        onModaleAttivitaClose() {
            this.editingAttivita = null;
            this.parentActivityForNew = null;
            this.defaultRowIdForNew = null;
        },
        async onElimina(attivita) {
            if (!confirm('Eliminare l\'attività "' + attivita.title + '"? Le attività figlie verranno promosse allo stesso livello.')) return;
            try {
                await axios.delete(route('modules.attivita.elimina-attivita-ordine', attivita.id));
                this.reloadAttivita();
            } catch (e) {
                alert('Errore durante l\'eliminazione.');
            }
        },
        async onAggiornaParent({ id, parent_id }) {
            try {
                await axios.post(route('modules.attivita.aggiorna-parent'), { id, parent_id });
                this.reloadAttivita();
            } catch (e) {
                alert('Errore durante l\'aggiornamento.');
            }
        },
        reloadAttivita() {
            const currentKey = this.selectedRigaKey;
            router.reload({
                only: ['attivita'],
                onSuccess: () => {
                    if (currentKey === null) {
                        this.selectedRiga = null;
                        this.selectedRigaKey = null;
                    } else {
                        const riga = this.righe.find(r => r.row_id === currentKey);
                        if (riga) {
                            this.selectedRiga = riga;
                            this.selectedRigaKey = currentKey;
                        }
                    }
                },
            });
        },
        dateStatus(value) {
            if (!value) return null;
            const normalized = String(value).substring(0, 10);
            const date = new Date(normalized + 'T00:00:00');
            if (isNaN(date)) return null;
            date.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (date.getTime() === today.getTime()) return 'oggi';
            if (date < today) return 'scaduto';
            return null;
        },
        async apriRegistroModal(riga = null) {
            this.rigaRegistroCorrente = riga;
            this.showRegistroModal = true;
            this.selectedRegistroId = null;
            this.registroColumns = [];
            this.registroColumnsOptions = {};
            this.registroFormValues = {};
            this.registroComboboxSearch = {};
            this.registroComboboxOpen = {};
            this.registroCustomerColumnId = null;
            this.registroCustomerFilterValue = '';
            this.registroRecordEsistenti = [];
            this.registroRecordPaginationTotal = 0;
            this.registroFormError = null;

            if (this.registri.length === 0) {
                this.loadingRegistri = true;
                try {
                    const resp = await axios.get(route('modules.registri-personalizzati.api.list'));
                    this.registri = resp.data.registries || [];
                } catch (e) {
                    this.registroFormError = 'Errore nel caricamento dei registri.';
                } finally {
                    this.loadingRegistri = false;
                }
            }
        },

        chiudiRegistroModal() {
            this.showRegistroModal = false;
            this.registroFormError = null;
        },

        async onRegistroChange() {
            this.registroColumns = [];
            this.registroColumnsOptions = {};
            this.registroFormValues = {};
            this.registroComboboxSearch = {};
            this.registroComboboxOpen = {};
            this.registroCustomerColumnId = null;
            this.registroRecordEsistenti = [];
            this.registroRecordPaginationTotal = 0;
            this.registroFormError = null;

            if (!this.selectedRegistroId) return;

            this.loadingRegistroColumns = true;
            try {
                const resp = await axios.get(route('modules.registri-personalizzati.api.detail', this.selectedRegistroId));
                this.registroColumns = resp.data.columns || [];
                this.registroColumnsOptions = resp.data.options || {};

                const customerNames = ['ragione sociale', 'cliente', 'customer', 'company', 'azienda', 'ditta', 'denominazione'];
                const customerCol = this.registroColumns.find(c => customerNames.includes(c.name.toLowerCase().trim()));
                this.registroCustomerColumnId = customerCol ? customerCol.id : null;

                const isQueryType = customerCol && ['select', 'magoquery', 'portalquery'].includes(customerCol.type);
                const customerCode = this.ordine.Customer || '';
                const customerLabel = this.ordine.CompanyName || this.ordine.Customer || '';
                this.registroCustomerFilterValue = isQueryType ? customerCode : customerLabel;

                const values = {};
                const comboSearch = {};
                const comboOpen = {};

                this.registroColumns.forEach(col => {
                    if (customerCol && col.id === customerCol.id) {
                        values[col.id] = isQueryType ? customerCode : customerLabel;
                    } else {
                        values[col.id] = '';
                    }
                    if (['select', 'magoquery', 'portalquery'].includes(col.type)) {
                        comboSearch[col.id] = (customerCol && col.id === customerCol.id) ? customerLabel : '';
                        comboOpen[col.id] = false;
                    }
                });
                this.registroFormValues = values;
                this.registroComboboxSearch = comboSearch;
                this.registroComboboxOpen = comboOpen;
            } catch (e) {
                this.registroFormError = 'Errore nel caricamento delle colonne.';
            } finally {
                this.loadingRegistroColumns = false;
            }

            if (this.registroCustomerColumnId !== null) {
                await this.caricaRecordEsistenti();
            }
        },

        async caricaRecordEsistenti() {
            const customerValue = this.registroCustomerFilterValue;
            if (!customerValue || this.registroCustomerColumnId === null) return;

            const filtersPayload = { [this.registroCustomerColumnId]: customerValue };

            this.loadingRegistroRecords = true;
            this.registroRecordEsistenti = [];
            this.registroRecordPaginationTotal = 0;
            try {
                const resp = await axios.get(
                    route('modules.registri-personalizzati.records.index', this.selectedRegistroId),
                    { params: { filters: JSON.stringify(filtersPayload), per_page: 10 } }
                );
                this.registroRecordEsistenti = resp.data.rows || [];
                this.registroRecordPaginationTotal = resp.data.pagination?.total || 0;
            } catch (e) {
                // silenzioso: la tabella resta vuota
            } finally {
                this.loadingRegistroRecords = false;
            }
        },

        getRegistroColumnOptions(column) {
            const id = String(column.id);
            return this.registroColumnsOptions[id] || this.registroColumnsOptions[column.id] || [];
        },

        getRegistroRecordCellValue(row, col) {
            const raw = row.values[col.id]?.raw;
            if (!raw) return '-';
            if (['select', 'magoquery', 'portalquery'].includes(col.type)) {
                const opts = this.getRegistroColumnOptions(col);
                const opt = opts.find(o => String(o.value) === String(raw));
                return opt ? opt.label : raw;
            }
            if (col.type === 'date') {
                try {
                    return new Date(raw).toLocaleDateString('it-IT');
                } catch (e) {
                    return raw;
                }
            }
            return raw;
        },

        getRegistroColumnFilteredOptions(column) {
            const opts = this.getRegistroColumnOptions(column);
            const search = (this.registroComboboxSearch[column.id] || '').toLowerCase().trim();
            if (!search) return opts;
            return opts.filter(o => o.label.toLowerCase().includes(search));
        },

        onComboboxInput(columnId) {
            this.registroFormValues[columnId] = '';
            this.registroComboboxOpen[columnId] = true;
        },

        selezionaOpzioneCombobox(columnId, opt) {
            this.registroFormValues[columnId] = opt.value;
            this.registroComboboxSearch[columnId] = opt.label;
            this.registroComboboxOpen[columnId] = false;
        },

        chiudiCombobox(columnId) {
            setTimeout(() => {
                this.registroComboboxOpen[columnId] = false;
            }, 150);
        },

        async salvaRegistroRecord() {
            if (!this.selectedRegistroId) return;

            this.salvandoRegistro = true;
            this.registroFormError = null;
            try {
                await axios.post(
                    route('modules.registri-personalizzati.records.store', this.selectedRegistroId),
                    { values: this.registroFormValues }
                );

                const values = {};
                const comboSearch = {};
                const customerLabel = this.ordine.CompanyName || this.ordine.Customer || '';
                this.registroColumns.forEach(col => {
                    values[col.id] = (col.id === this.registroCustomerColumnId) ? this.registroCustomerFilterValue : '';
                    if (['select', 'magoquery', 'portalquery'].includes(col.type)) {
                        comboSearch[col.id] = (col.id === this.registroCustomerColumnId) ? customerLabel : '';
                    }
                });
                this.registroFormValues = values;
                this.registroComboboxSearch = { ...this.registroComboboxSearch, ...comboSearch };

                if (this.registroCustomerColumnId !== null) {
                    await this.caricaRecordEsistenti();
                }
            } catch (e) {
                const data = e.response?.data;
                this.registroFormError = data?.message || data?.error || 'Errore durante il salvataggio.';
            } finally {
                this.salvandoRegistro = false;
            }
        },
    },
};
</script>


<style scoped>
.action-panel-fade-enter-active,
.action-panel-fade-leave-active {
    transition: opacity 0.18s ease;
}

.action-panel-fade-enter-from,
.action-panel-fade-leave-to {
    opacity: 0;
}

.action-panel-slide-enter-active,
.action-panel-slide-leave-active {
    transition: transform 0.22s ease;
}

.action-panel-slide-enter-from,
.action-panel-slide-leave-to {
    transform: translateX(100%);
}

.action-panel-slide-enter-to,
.action-panel-slide-leave-from {
    transform: translateX(0);
}
.scaduto {
    color: #dc2626;
    font-weight: 600;
    background-color: #ffe6e6; /* Opzionale: un leggero sfondo rosso chiaro per evidenziarlo ancora di più */
    padding: 2px 4px;        /* Opzionale: spaziatura per lo sfondo */
    border-radius: 4px;      /* Opzionale: angoli smussati per lo sfondo */
}
.oggi {
    color: #eb6425;
    font-weight: 600;
    background-color: #fff4e6; /* Opzionale: un leggero sfondo giallo chiaro per evidenziarlo ancora di più */
    padding: 2px 4px;        /* Opzionale: spaziatura per lo sfondo */
    border-radius: 4px;      /* Opzionale: angoli smussati per lo sfondo */
}
</style>
